// Четыре экрана с плиток Профиля: Чумбы, Достижения, Рефералы, Инвентарь.
// Все компактные — основная структура есть, детали (полные списки, модалки)
// добавим по запросу. Каждый в одном файле, чтобы не плодить файлы по 50 строк.

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { useTelegram } from '../telegram/useTelegram';
import { useGameStore } from '../store/useGameStore';
import { UserAvatar } from '../components/UserAvatar';
import { setActiveAvatar, setActiveTitle, listMyReferrals, type MyReferralsResponse,
  openPackApi, getProfile, type OpenedCard, type RarityApi,
  searchPlayers, getFriends, getIncomingFriendRequests, getSentFriendRequests,
  requestFriend, respondFriendRequest, removeFriend,
  type FriendItem, type IncomingRequestItem, type PlayerSearchItem, type SentRequestItem } from '../api/client';
import { useTooltipFlow } from '../tutorial/useTooltipFlow';
import { useTr } from '../i18n/useTr';
import { ContainerIcon } from '../components/ContainerIcon';
import { ScriptsIcon } from '../components/ScriptsIcon';
import { trTitleText, trLeague } from '../i18n/serverStrings';
import { useToast } from '../components/Toast';
import { CrateOpeningScene, containerTypeToCrateType } from '../components/CrateOpeningScene';
import { useAnimationsEnabled } from '../hooks/useAnimationsEnabled';
import styles from './ProfileTileScreens.module.css';

// ============================================================
// 1. ЧУМБЫ (друзья) — ДРУГ.2: реальный flow с сервера.
// ============================================================
// Три таба:
//   ЧУМБЫ — мои принятые друзья (GET /api/friends)
//   ЗАЯВКИ — входящие pending (GET /api/friends/incoming) + бейдж количества
//   ПОИСК — поиск по нику (GET /api/players/search?q=) + кнопка «Добавить»
// На каждой строке свайп-/тап-acции согласно контексту.

const FACTION_COLOR: Record<string, string> = {
  deus: 'var(--deus)', syntetics: 'var(--syn)', global: 'var(--gsec)', livlife: 'var(--liv)', neutral: 'var(--neu)'
};

type FriendsTab = 'list' | 'incoming' | 'search';

export function FriendsScreen() {
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<FriendsTab>('list');

  // ФТУЕ.6: tooltip-флоу первого входа в Чумбы.
  const tr = useTr();
  useTooltipFlow('friends', () => [
    {
      id: 'fr-intro',
      coach: {
        title: tr('Чумбы', 'Chooms'),
        body: tr(
          'Это твои внутриигровые друзья. Лимит — 100. Тут видишь онлайн, лигу и активность каждого. Тап по чумбе → его профиль с матчами и колодами.',
          'Your in-game friends. Limit — 100. See who is online, their league and activity. Tap a choom → their profile with matches and decks.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'fr-add',
      coach: {
        title: tr('Как добавить', 'How to add'),
        body: tr(
          'Вкладка «Поиск» — ищи по нику. Шлёшь заявку → чумба подтверждает. Альтернатива: пригласи через Сигнал (реф-код) — он автоматом станет чумбой при регистрации.',
          '«Search» tab — find by handle. Send a request → choom accepts. Alternative: invite via Beacon (referral code) — they\'ll auto-become a choom on signup.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'fr-invite',
      coach: {
        title: tr('Пригласить на матч', 'Invite to a match'),
        body: tr(
          'Тап «Бой» на чумбе → отправляется приглашение в Casual-режим. Если чумба онлайн и принял — попадёте в матч друг против друга, в обход обычного матчмейкинга.',
          'Tap «Fight» on a choom → invite to Casual mode is sent. If choom is online and accepts — you go into a match against each other, bypassing regular matchmaking.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  // Списки.
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [limit, setLimit] = useState<number>(100);
  const [incoming, setIncoming] = useState<IncomingRequestItem[]>([]);
  const [sent, setSent] = useState<SentRequestItem[]>([]);

  // Поиск.
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<PlayerSearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  // Кому уже отправили (оптимистично, чтобы кнопка не дублировалась).
  const [sentLocal, setSentLocal] = useState<Set<number>>(new Set());

  const refreshAll = useCallback(async () => {
    try {
      const [f, inc, st] = await Promise.all([
        getFriends(), getIncomingFriendRequests(), getSentFriendRequests()
      ]);
      setFriends(f.items);
      setLimit(f.limit);
      setIncoming(inc.items);
      setSent(st.items);
    } catch (e) { /* swallow */ }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Поиск debounced.
  useEffect(() => {
    if (tab !== 'search') return;
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const r = await searchPlayers(q);
        if (!cancelled) setResults(r.items);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, tab]);

  const onAdd = async (p: PlayerSearchItem) => {
    setSentLocal(prev => new Set([...prev, p.id]));
    try {
      const r = await requestFriend(p.id);
      toast.show(r.silent ? tr('Запрос отправлен', 'Request sent') : tr('Запрос отправлен ✓', 'Request sent ✓'), 'green');
      await refreshAll();
    } catch (e: any) {
      const code = e?.payload?.error;
      const msg = code === 'too_many'        ? tr('Не больше 10 заявок в сутки', 'Max 10 requests per day')
                : code === 'already_friends' ? tr('Вы уже чумбы', 'Already chooms')
                : code === 'already_pending' ? tr('Заявка уже отправлена', 'Request already sent')
                : code === 'sender_full'     ? tr('У тебя 100/100 чумб', 'You\'re at 100/100 chooms')
                : code === 'recipient_full'  ? tr('У игрока полный список', 'Player\'s list is full')
                : tr('Не удалось отправить', 'Failed to send');
      toast.show(msg, 'red');
      setSentLocal(prev => { const next = new Set(prev); next.delete(p.id); return next; });
    }
  };

  const onRespond = async (requestId: number, action: 'accept' | 'decline_temp' | 'decline_forever') => {
    try {
      await respondFriendRequest(requestId, action);
      const label = action === 'accept' ? tr('Принято ✓', 'Accepted ✓')
                  : action === 'decline_temp' ? tr('Отклонено', 'Declined')
                  : tr('Заблокировано', 'Blocked');
      toast.show(label, 'green');
      await refreshAll();
    } catch {
      toast.show(tr('Не удалось ответить', 'Failed to respond'), 'red');
    }
  };

  const onRemove = async (friendId: number) => {
    try {
      await removeFriend(friendId);
      toast.show(tr('Удалён из чумб', 'Removed from chooms'), 'green');
      await refreshAll();
    } catch { toast.show(tr('Не удалось удалить', 'Failed to remove'), 'red'); }
  };

  const friendIdSet = useMemo(() => new Set(friends.map(f => f.id)), [friends]);
  const sentIdSet = useMemo(() => new Set(sent.filter(s => s.status === 'pending').map(s => s.id)), [sent]);

  return (
    <ScreenLayout title={tr('ЧУМБЫ', 'CHOOMS')} backTo="/profile">
      {/* Табы */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {([
          { k: 'list',     label: `${tr('Чумбы', 'Chooms')} ${friends.length}/${limit}` },
          { k: 'incoming', label: `${tr('Заявки', 'Requests')}${incoming.length > 0 ? ' ' + incoming.length : ''}` },
          { k: 'search',   label: tr('Поиск', 'Search') }
        ] as const).map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className="cd"
            style={{
              flex: 1,
              padding: '8px 10px',
              background: tab === t.k ? 'var(--neon-cy, #3DF0FF)' : 'transparent',
              color: tab === t.k ? '#06131A' : 'var(--t-mid)',
              border: `0.5px solid ${tab === t.k ? 'var(--neon-cy, #3DF0FF)' : 'var(--line, #555)'}`,
              fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
              cursor: 'pointer'
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* TAB: list */}
      {tab === 'list' && (
        <div className={styles.list}>
          {friends.length === 0 && (
            <div className={styles.empty}>
              {tr(
                'Чумб пока нет. Перейди в «Поиск», чтобы найти игроков по нику.',
                'No chooms yet. Go to «Search» to find players by handle.'
              )}
            </div>
          )}
          {friends.map(f => (
            <div key={f.id} className={`${styles.row} cut-s`}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/player/${f.id}`)}>
              <div className={styles.avatar} style={{ borderColor: FACTION_COLOR[f.faction] || 'var(--neu)' }} />
              <div className={styles.rowInfo}>
                <div className={styles.rowName}>{f.nick}</div>
                <div className={styles.rowSub}>{f.faction.toUpperCase()}</div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); if (confirm(tr(`Удалить ${f.nick} из чумб?`, `Remove ${f.nick} from chooms?`))) onRemove(f.id); }}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'var(--t-lo)', fontSize: 18, cursor: 'pointer', padding: '0 8px'
                }}
                title={tr('Удалить из чумб', 'Remove from chooms')}
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* TAB: incoming */}
      {tab === 'incoming' && (
        <div className={styles.list}>
          {incoming.length === 0 && (
            <div className={styles.empty}>{tr('Входящих заявок нет.', 'No incoming requests.')}</div>
          )}
          {incoming.map(r => (
            <div key={r.requestId} className={`${styles.row} cut-s`} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                onClick={() => navigate(`/player/${r.id}`)}>
                <div className={styles.avatar} style={{ borderColor: FACTION_COLOR[r.faction] || 'var(--neu)' }} />
                <div className={styles.rowInfo}>
                  <div className={styles.rowName}>{r.nick}</div>
                  <div className={styles.rowSub}>{r.faction.toUpperCase()}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => onRespond(r.requestId, 'accept')}
                  className="cd"
                  style={{ flex: 1, padding: '7px', background: 'var(--neon-li, #b4ff00)', color: '#06131A', border: 'none', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer' }}>
                  {tr('Принять', 'Accept')}
                </button>
                <button onClick={() => onRespond(r.requestId, 'decline_temp')}
                  className="cd"
                  style={{ padding: '7px 10px', background: 'transparent', color: 'var(--t-mid)', border: '0.5px solid var(--line, #555)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer' }}>
                  {tr('Отклонить', 'Decline')}
                </button>
                <button onClick={() => { if (confirm(tr(
                  `Заблокировать ${r.nick} навсегда? Этот игрок больше не сможет отправлять тебе заявки.`,
                  `Block ${r.nick} permanently? They won't be able to send you requests anymore.`
                ))) onRespond(r.requestId, 'decline_forever'); }}
                  className="cd"
                  style={{ padding: '7px 10px', background: 'transparent', color: '#ff7a7a', border: '0.5px solid #ff7a7a', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer' }}>
                  ⛔
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB: search */}
      {tab === 'search' && (
        <>
          <div className={styles.searchBar}>
            <input
              type="text"
              className={`${styles.searchInput} cb`}
              placeholder={tr('Поиск по нику (мин. 2 символа)…', 'Search by handle (min 2 chars)…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className={styles.list}>
            {searching && <div className={styles.empty}>{tr('Ищу…', 'Searching…')}</div>}
            {!searching && search.trim().length >= 2 && results.length === 0 && (
              <div className={styles.empty}>{tr('Никого с таким ником не найдено.', 'No one with that handle.')}</div>
            )}
            {results.map(p => {
              const isFriend = friendIdSet.has(p.id);
              const isPending = sentIdSet.has(p.id) || sentLocal.has(p.id);
              return (
                <div key={p.id} className={`${styles.row} cut-s`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/player/${p.id}`)}>
                  <div className={styles.avatar} style={{ borderColor: FACTION_COLOR[p.faction] || 'var(--neu)' }} />
                  <div className={styles.rowInfo}>
                    <div className={styles.rowName}>{p.nick}</div>
                    <div className={styles.rowSub}>{(p.faction || '').toUpperCase()}</div>
                  </div>
                  {isFriend ? (
                    <span style={{ fontSize: 11, color: 'var(--neon-li, #b4ff00)' }}>{tr('✓ ЧУМБА', '✓ CHOOM')}</span>
                  ) : isPending ? (
                    <span style={{ fontSize: 11, color: 'var(--t-mute, #888)' }}>{tr('ОТПРАВЛЕНО', 'SENT')}</span>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); onAdd(p); }}
                      className="cd"
                      style={{ padding: '6px 12px', background: 'var(--neon-cy, #3DF0FF)', color: '#06131A', border: 'none', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer' }}>
                      + {tr('В чумбы', 'Add choom')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </ScreenLayout>
  );
}

// ============================================================
// 2. ДОСТИЖЕНИЯ — ui-spec.md §3
// ============================================================

interface Achievement {
  id: string;
  name: string;
  desc: string;
  unlocked: boolean;
  category: 'combat' | 'faction' | 'league' | 'collector' | 'social';
  progress?: { current: number; total: number };
}

// Будет подключено к серверу (GET /api/achievements — endpoint в работе).
// ДО.1: каталог 15 достижений тянется с сервера через /api/achievements.
// Фильтры по статусу и категории. Если completed && !claimed — кнопка
// «Забрать» на карточке.

type AchvFilter = 'all' | 'unlocked' | 'locked' | 'combat' | 'collection' | 'social' | 'season' | 'misc';

function buildCategoryLabel(t: (ru: string, en: string) => string): Record<string, string> {
  return {
    combat:     t('Боевые', 'Combat'),
    collection: t('Коллекция', 'Collection'),
    social:     t('Социалка', 'Social'),
    season:     t('Сезон', 'Season'),
    misc:       t('Прочее', 'Misc')
  };
}

const TIER_COLOR: Record<string, string> = {
  bronze: '#CD7F32', silver: '#B6BACA', gold: '#FFB13D'
};

// Описание награды строкой для карточки достижения.
function describeReward(p: any, t: (ru: string, en: string) => string): string {
  if (!p || typeof p !== 'object') return '—';
  const parts: string[] = [];
  if (p.scripts) parts.push(`+${p.scripts} ◆`);
  if (p.container) {
    const c = p.container.count > 1 ? `${p.container.count}× ` : '';
    parts.push(`${c}${t('ШК', 'CC')} «${containerLabel(p.container.type, t)}»`);
  }
  if (p.title) parts.push(`${t('титул', 'title')} «${p.title.text}»`);
  if (p.avatar) parts.push(`${t('аватар', 'avatar')} «${p.avatar}»`);
  return parts.join(' + ') || '—';
}

export function AchievementsScreen() {
  const [filter, setFilter] = useState<AchvFilter>('all');
  const [items, setItems] = useState<import('../api/client').AchievementDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  // БУГ.11: picker фракции при claim с requires_faction_choice.
  const [factionPick, setFactionPick] = useState<{ achievementId: number; title: string } | null>(null);

  // ФТУЕ.6: tooltip-флоу первого входа в Достижения.
  const tr = useTr();
  useTooltipFlow('achievements', () => [
    {
      id: 'ach-intro',
      coach: {
        title: tr('Достижения', 'Achievements'),
        body: tr(
          'Долгосрочные цели — побеждай в боях, прокачивай Героев, собирай карты, расти по уровням. За каждое достижение — Скрипты, ШК, иногда титулы и косметика.',
          'Long-term goals — win battles, level up Heroes, collect cards, grow in levels. Each achievement — Scripts, Cipher Crates, sometimes titles and cosmetics.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'ach-progress',
      coach: {
        title: tr('Прогресс', 'Progress'),
        body: tr(
          'Прогресс копится автоматически — играй и побеждай. Когда достижение выполнено — рядом загорится «Забрать». Кнопка кликабельна, пока награду не взял.',
          "Progress racks up automatically — play and win. When done — «Claim» button lights up next to it. Stays clickable until you take the reward."
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'ach-tabs',
      coach: {
        title: tr('Фильтры', 'Filters'),
        body: tr(
          'Сверху — фильтры: «Все», «В работе», «Готовы», «Забраны». Так удобнее видеть что забрать и за чем гнаться. Часть достижений — сезонные (сбрасываются), часть — пожизненные.',
          'Top filters: «All», «In progress», «Ready», «Claimed». Easier to see what to grab and what to chase. Some achievements are seasonal (reset), some are lifetime.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);
  const { hapticTap, hapticSuccess, hapticError, alert } = useTelegram();

  const reload = useCallback(async () => {
    try {
      const { listAchievements } = await import('../api/client');
      const data = await listAchievements();
      setItems(data);
    } catch (e) {
      console.warn('achievements load failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const unlocked = items.filter(a => a.completed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'unlocked') return items.filter(a => a.completed);
    if (filter === 'locked')   return items.filter(a => !a.completed);
    return items.filter(a => a.category === filter);
  }, [filter, items]);

  const filters: { key: AchvFilter; label: string }[] = [
    { key: 'all',        label: tr('Все', 'All') },
    { key: 'unlocked',   label: tr('Открытые', 'Unlocked') },
    { key: 'locked',     label: tr('Закрытые', 'Locked') },
    { key: 'combat',     label: tr('Бой', 'Combat') },
    { key: 'collection', label: tr('Коллекция', 'Collection') },
    { key: 'social',     label: tr('Социалка', 'Social') },
    { key: 'season',     label: tr('Сезон', 'Season') },
    { key: 'misc',       label: tr('Прочее', 'Misc') }
  ];
  const categoryLabel = buildCategoryLabel(tr);
  const isEn = tr('ru', 'en') === 'en';

  const onClaim = async (id: number, faction?: 'deus' | 'syntetics' | 'global' | 'livlife') => {
    hapticTap();
    setBusyId(id);
    try {
      const { claimAchievement } = await import('../api/client');
      const r = await claimAchievement(id, faction);
      // БУГ.11: сервер просит выбор фракции — открываем picker.
      if (r.requires_faction_choice) {
        const ach = items.find((x) => x.id === id);
        const title = ach ? (isEn ? ach.title_en : ach.title_ru) : '?';
        setFactionPick({ achievementId: id, title });
        setBusyId(null);
        return;
      }
      // БУГ.13: alert убран — UI обновится через reload(), haptic уведомит.
      hapticSuccess();
      await reload();
    } catch (e: any) {
      hapticError();
      alert(tr(`Не удалось: ${e?.message || 'ошибка'}`, `Failed: ${e?.message || 'error'}`));
    } finally {
      setBusyId(null);
    }
  };
  const onFactionPicked = async (faction: 'deus' | 'syntetics' | 'global' | 'livlife') => {
    if (!factionPick) return;
    const pickedId = factionPick.achievementId;
    setFactionPick(null);
    await onClaim(pickedId, faction);
  };

  return (
    <ScreenLayout
      title={tr('ДОСТИЖЕНИЯ', 'ACHIEVEMENTS')}
      backTo="/profile"
      onHelpOverride={() => alert(tr(
        'Чумба, тебе реально тут надо что-то объяснять?',
        "Choom, do you really need an explanation here?"
      ))}
    >
      <div className={styles.progressBar}>
        <div className={styles.progressTop}>
          <span className={styles.progressLabel}>{tr('Открыто', 'Unlocked')}</span>
          <span className={styles.progressCount}>
            <strong>{unlocked}</strong> <span style={{ color: 'var(--t-lo)' }}>/ {total}</span>
          </span>
          <span className={styles.progressPct}>{pct}%</span>
        </div>
        <div className={styles.progressBarBg}>
          <div className={styles.progressBarFill} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className={styles.filterScroll}>
        {filters.map(f => (
          <button
            key={f.key}
            className={`${styles.filterPill} ${filter === f.key ? styles.filterPillActive : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className={styles.list}>
        {loading && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t-md)' }}>{tr('Загрузка…', 'Loading…')}</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t-md)' }}>{tr('Пусто', 'Empty')}</div>
        )}
        {filtered.map(a => {
          const canClaim = a.completed && !a.claimed;
          const tierColor = a.tier ? TIER_COLOR[a.tier] : 'var(--t-md)';
          // EN-поля приходят с сервера как title_en / description_en (если есть).
          const title = isEn ? ((a as any).title_en || a.title_ru) : a.title_ru;
          const description = isEn ? ((a as any).description_en || a.description_ru) : a.description_ru;
          return (
            <div
              key={a.id}
              className={`${styles.achvRow} ${!a.completed ? styles.achvLocked : ''} cut-s`}
              style={canClaim ? { boxShadow: 'inset 0 0 0 1px var(--neon-am)' } : undefined}
            >
              <div
                className={styles.achvIcon}
                style={{ color: tierColor, fontSize: 22 }}
                title={a.tier || ''}
              >
                {a.completed ? '🏆' : '🔒'}
              </div>
              <div className={styles.rowInfo}>
                <div className={styles.rowName}>{title}</div>
                <div className={styles.rowSub}>
                  {description} <span style={{ color: 'var(--t-lo)', fontSize: 10 }}>· {categoryLabel[a.category] || a.category}</span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--neon-am)', marginTop: 3 }}>
                  {tr('Награда', 'Reward')}: {describeReward(a.reward_payload, tr)}
                </div>
                {!a.completed && (
                  <div className={styles.achvProgress}>
                    <div className={styles.achvProgressBar}>
                      <div
                        className={styles.achvProgressFill}
                        style={{ width: `${Math.min(100, (a.progress / a.target_value) * 100)}%` }}
                      />
                    </div>
                    <span className={styles.achvProgressTxt}>
                      {a.code === 'market_usdt_100'
                        ? `$${(a.progress / 100).toFixed(2)} / $${(a.target_value / 100).toFixed(0)}`
                        : `${a.progress} / ${a.target_value}`}
                    </span>
                  </div>
                )}
                {canClaim && (
                  <button
                    onClick={() => onClaim(a.id)}
                    disabled={busyId === a.id}
                    style={{
                      marginTop: 6,
                      padding: '6px 12px',
                      fontSize: 11,
                      background: 'var(--neon-am)',
                      color: 'var(--bg)',
                      border: 'none',
                      fontFamily: 'inherit',
                      letterSpacing: 1,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >{tr('ЗАБРАТЬ', 'CLAIM')}</button>
                )}
                {a.claimed && (
                  <span style={{ marginTop: 4, fontSize: 10, color: 'var(--t-lo)' }}>{tr('✓ получено', '✓ received')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* БУГ.11: picker фракции для main_faction ШК (requires_faction_choice). */}
      {factionPick && (
        <AchievementFactionPicker
          title={factionPick.title}
          onPick={onFactionPicked}
          onCancel={() => setFactionPick(null)}
        />
      )}
    </ScreenLayout>
  );
}

// БУГ.11: модалка выбора фракции при claim ачивки с container 'main_faction'.
// Скопировано в духе магазинного PackPreviewModal (MainScreens.tsx).
function AchievementFactionPicker({
  title, onPick, onCancel
}: {
  title: string;
  onPick: (faction: 'deus' | 'syntetics' | 'global' | 'livlife') => void;
  onCancel: () => void;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const factions: Array<{ id: 'deus' | 'syntetics' | 'global' | 'livlife'; label_ru: string; label_en: string; color: string }> = [
    { id: 'deus',      label_ru: 'Деус',      label_en: 'Deus',       color: 'var(--deus, #3D7DFF)' },
    { id: 'syntetics', label_ru: 'Синтетикс', label_en: 'Syntetics',  color: 'var(--syn, #C93DFF)' },
    { id: 'global',    label_ru: 'Глобал',    label_en: 'Global',     color: 'var(--gsec, #FF8A1F)' },
    { id: 'livlife',   label_ru: 'Лив Лайф',  label_en: 'Liv Life',   color: 'var(--liv, #FF3D3D)' }
  ];
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(7, 7, 11, 0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
        backdropFilter: 'blur(8px)'
      }}
    >
      <div
        style={{
          maxWidth: 420, width: '100%',
          background: 'var(--bg-2, #101018)',
          boxShadow: 'inset 0 0 0 1px var(--neon-am, #FFB13D), 0 0 40px rgba(255, 177, 61, 0.4)',
          padding: '20px 18px',
          clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
        }}
      >
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 16, fontWeight: 700, letterSpacing: '0.1em',
          color: 'var(--neon-am, #FFB13D)', marginBottom: 4
        }}>{tr('ВЫБЕРИ КОРПУ', 'PICK CORP')}</div>
        <div style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 12, letterSpacing: '0.06em',
          color: 'var(--t-md, #A0A4B4)', marginBottom: 8
        }}>{title}</div>
        <div style={{
          fontFamily: "'Inter', sans-serif", fontSize: 12,
          color: 'var(--t-md, #A0A4B4)', marginBottom: 14, lineHeight: 1.4
        }}>{tr(
          'ШК будет выдан из выбранной корпы.',
          'Container will be granted from the picked corp.'
        )}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 14 }}>
          {factions.map((f) => (
            <button
              key={f.id}
              onClick={() => onPick(f.id)}
              style={{
                padding: '14px 12px',
                background: 'var(--bg-0, #07070B)',
                border: 0,
                boxShadow: `inset 0 0 0 1px ${f.color}`,
                color: f.color,
                fontFamily: "'Chakra Petch', sans-serif",
                fontSize: 14, fontWeight: 700, letterSpacing: '0.06em',
                cursor: 'pointer',
                clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
                touchAction: 'manipulation'
              }}
            >{isEn ? f.label_en : f.label_ru}</button>
          ))}
        </div>
        <button
          onClick={onCancel}
          style={{
            width: '100%', padding: 12,
            background: 'transparent', border: 0,
            boxShadow: 'inset 0 0 0 0.5px var(--t-md)',
            color: 'var(--t-md, #A0A4B4)',
            fontFamily: "'Chakra Petch', sans-serif",
            fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase',
            cursor: 'pointer',
            clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
          }}
        >{tr('Отмена', 'Cancel')}</button>
      </div>
    </div>
  );
}

// ============================================================
// 3. РЕФЕРАЛЫ — ui-spec.md §3
// ============================================================

// Форматирует «когда был активен» по last_seen_at.
function formatLastSeen(iso: string | null, isActive: boolean, t: (ru: string, en: string) => string): string {
  if (!iso) return t('не заходил', 'never logged in');
  if (isActive) return t('активен', 'active');
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 1) return t('был недавно', 'was here recently');
  if (days < 30) return t(`был ${days} д. назад`, `was here ${days}d ago`);
  const months = Math.floor(days / 30);
  return t(`был ${months} мес. назад`, `was here ${months}mo ago`);
}

export function ReferralsScreen() {
  const { hapticTap, alert } = useTelegram();
  const code = useGameStore(s => s.player.referralCode) || '—';
  const [data, setData] = useState<MyReferralsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // ФТУЕ.6: tooltip-флоу первого входа в Рефералы (по диздоку §11.4).
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const SI = (props?: { size?: number }) => (
    <span style={{ display: 'inline-flex', verticalAlign: '-2px', margin: '0 1px' }}>
      <ScriptsIcon size={props?.size ?? 14} />
    </span>
  );
  const CI = ({ type }: { type: 'main_common' | 'advanced_common' }) => (
    <span style={{ display: 'inline-flex', verticalAlign: '-3px', margin: '0 1px' }}>
      <ContainerIcon type={type} size={16} />
    </span>
  );
  // РЕФ.3 (Этап 77): упрощённый флоу — 2 шага. Реферал засчитывается, когда
  // приглашённый прошёл/скипнул обучение; награды двусторонние и мгновенные.
  useTooltipFlow('referrals', () => [
    {
      id: 'ref-intro',
      coach: {
        title: tr('Сигнал · 1/2', 'Beacon · 1/2'),
        body: isEn ? (
          <>
            Top — your Beacon (referral code). Share it: when a choom opens via
            <b> t.me/cyvercorpsbot/app?startapp=CODE</b> and finishes (or skips)
            the tutorial — they get <b>1</b><CI type="main_common" /> Standard CC
            and <b>+20</b><SI />, and <b>you</b> get <b>1</b>
            <CI type="advanced_common" /> Advanced CC. Instantly.
          </>
        ) : (
          <>
            Сверху — твой Сигнал (реф-код). Поделись им: когда чумба зайдёт по
            ссылке <b>t.me/cyvercorpsbot/app?startapp=КОД</b> и пройдёт (или
            скипнет) обучение — он получит <b>1</b><CI type="main_common" />
            Основной общий ШК и <b>+20</b><SI />, а <b>ты</b> — <b>1</b>
            <CI type="advanced_common" /> Продвинутый общий ШК. Сразу.
          </>
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'ref-cashback',
      coach: {
        title: tr('Кэшбэк с Войн · 2/2', 'War cashback · 2/2'),
        body: isEn ? (
          <>
            And forever after: <b>1% of every Corp Wars win</b> by your referral
            comes to you in <b>USDT</b> — taken from the platform fee, so it
            doesn't reduce their winnings. The more chooms you bring who play
            for real money — the more passive income for you.
          </>
        ) : (
          <>
            И навсегда дальше: <b>1% с каждого выигрыша</b> твоего реферала в
            Войне Корпораций капает тебе в <b>USDT</b> — берётся из комиссии
            платформы, выигрыш реферала не уменьшается. Чем больше приведёшь
            чумб, играющих на реальные деньги, — тем больше пассивный доход.
          </>
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await listMyReferrals();
        if (!cancelled) setData(r);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || tr('Не удалось загрузить', 'Failed to load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tr]);

  return (
    <ScreenLayout title={tr('РЕФЕРАЛЫ', 'REFERRALS')} backTo="/profile">
      <div className={styles.refCode}>
        <div className={styles.refCodeLabel}>{tr('ТВОЙ СИГНАЛ', 'YOUR BEACON')}</div>
        <div className={styles.refCodeRow}>
          <div className={styles.refCodeValue}>{code}</div>
          <button
            className={styles.refCodeBtn}
            onClick={() => { hapticTap(); navigator.clipboard?.writeText(code); alert(tr('Скопировано', 'Copied')); }}
          >
            📋
          </button>
          <button
            className={styles.refCodeBtn}
            onClick={() => {
              hapticTap();
              const text = tr(
                `Зацени «Корпы» — киберпанк-карточная игра в Telegram. Мой Сигнал: ${code}`,
                `Check out «Corps» — a cyberpunk card game on Telegram. My Beacon: ${code}`
              );
              // Прямая ссылка на Mini App: t.me/<bot>/<app>?startapp=КОД
              // Telegram передаст КОД в start_param при открытии TMA.
              const url = `https://t.me/cyvercorpsbot/app?startapp=${encodeURIComponent(code)}`;
              const tg = (window as any).Telegram?.WebApp;
              if (tg?.openTelegramLink) {
                const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
                tg.openTelegramLink(shareUrl);
                return;
              }
              if ((navigator as any).share) {
                (navigator as any).share({ title: tr('Корпы', 'Corps'), text, url }).catch(() => {});
                return;
              }
              navigator.clipboard?.writeText(`${text}\n${url}`);
              alert(tr('Ссылка скопирована — пошли её в любом мессенджере.', 'Link copied — share via any messenger.'));
            }}
          >
            ↗
          </button>
        </div>
      </div>

      <div className={styles.refSummary}>
        <SumCell value={data ? String(data.total) : '—'} label={tr('Всего', 'Total')} />
        <SumCell value={data ? String(data.active) : '—'} label={tr('Активных', 'Active')} highlight />
      </div>

      {/* РЕФ.3 (Этап 77): реальные награды вместо «скоро» + тиров. */}
      <div className={styles.refCap}>
        <div className={styles.refCapRow}>
          <span className={styles.refCapLabel}>{tr('НАГРАДЫ', 'REWARDS')}</span>
        </div>
        <div className={styles.refCapTiers}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            {tr('За прошедшего обучение:', 'Per referral who finishes tutorial:')}
            <ContainerIcon type="advanced_common" size={15} /> {tr('тебе', 'you')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', color: 'var(--t-md)' }}>
            {tr('ему:', 'them:')} <ContainerIcon type="main_common" size={15} /> + 20 <ScriptsIcon size={13} />
          </div>
          <div style={{ color: 'var(--neon-li, #B4FF00)' }}>
            + 1% {tr('с его выигрышей в Войне (USDT)', "of their Corp Wars wins (USDT)")}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="cd" style={{ color: 'var(--t-md)', fontSize: 10, letterSpacing: '0.1em', marginBottom: 8 }}>
          {tr('ПРИГЛАШЁННЫЕ ИГРОКИ', 'INVITED PLAYERS')}
        </div>

        {loading && (
          <div style={{ color: 'var(--t-lo)', textAlign: 'center', padding: 16, fontSize: 12 }}>
            {tr('Загрузка…', 'Loading…')}
          </div>
        )}

        {err && (
          <div style={{ color: '#ff7a7a', textAlign: 'center', padding: 16, fontSize: 12 }}>
            {tr('Ошибка', 'Error')}: {err}
          </div>
        )}

        {!loading && !err && data && data.list.length === 0 && (
          <div style={{
            color: 'var(--t-lo)', textAlign: 'center', padding: 24,
            fontSize: 12, fontStyle: 'italic', lineHeight: 1.5
          }}>
            {tr('Никого пока не пригласил.', "You haven't invited anyone yet.")}<br />
            {tr('Поделись своим Сигналом — кнопка ↗ выше.', 'Share your Beacon — ↗ button above.')}
          </div>
        )}

        {!loading && !err && data && data.list.length > 0 && (
          <div className={styles.list}>
            {data.list.map(r => {
              const color = (FACTION_COLOR as any)[r.faction] || 'var(--t-md)';
              return (
                <div key={r.id} className={`${styles.row} cut-s`}>
                  <div className={styles.avatar} style={{ borderColor: color }} />
                  <div className={styles.rowInfo}>
                    <div className={styles.rowName}>{r.nick}</div>
                    <div className={styles.rowSub}>
                      <span style={{ color: r.is_active ? 'var(--neon-li)' : 'var(--t-lo)' }}>
                        {formatLastSeen(r.last_seen_at, r.is_active, tr)}
                      </span>
                      <span style={{ color: 'var(--t-lo)' }}> · {trLeague(r.league)}</span>
                    </div>
                  </div>
                  <div className={styles.rowStatus}>{tr('ур.', 'lvl ')}{r.account_level}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ScreenLayout>
  );
}

function SumCell({ value, label, highlight }: { value: string; label: string; highlight?: boolean }) {
  return (
    <div className={styles.sumCell}>
      <div className={styles.sumVal} style={{ color: highlight ? 'var(--neon-li)' : 'var(--t-hi)' }}>{value}</div>
      <div className={styles.sumLbl}>{label}</div>
    </div>
  );
}

// ============================================================
// 4. ИНВЕНТАРЬ — ui-spec.md §3
// ============================================================

type InvTab = 'avatars' | 'titles' | 'frames' | 'card_backs' | 'emote_sets' | 'card_skins' | 'packs';

// Сопоставление вкладок инвентаря с cosmetic_type на сервере.
const INV_COSMETIC_TYPE: Record<InvTab, string | null> = {
  avatars: 'avatar',
  titles: null,   // прозвища в отдельной таблице player_titles
  frames: 'frame',
  card_backs: 'card_back',
  emote_sets: 'emote',
  card_skins: 'card_skin',
  packs: null     // ШК — это не косметика, отдельная сущность
};

function buildInvTabLabel(t: (ru: string, en: string) => string): Record<InvTab, string> {
  return {
    avatars: t('Аватары', 'Avatars'),
    titles: t('Прозвища', 'Titles'),
    frames: t('Рамки', 'Frames'),
    card_backs: t('Обложки', 'Card backs'),
    emote_sets: t('Эмоции', 'Emotes'),
    card_skins: t('Скины', 'Skins'),
    packs: t('ШК', 'CC')
  };
}

// МАГ.3: человекочитаемое имя ШК по `type` (для Инвентаря).
// Поддерживаем legacy-имена (`basic_*`, `main_faction`, `advanced_faction`),
// которые пришли из квестов/БП/стартового бандла.
function containerLabel(type: string, t: (ru: string, en: string) => string): string {
  if (type === 'main_common' || type === 'basic_common') return t('Основной общий', 'Standard common');
  if (type === 'advanced_common')                        return t('Продвинутый общий', 'Advanced common');
  if (type === 'main_faction')                           return t('Основной · выбери корпу', 'Standard · pick a corp');
  if (type === 'advanced_faction')                       return t('Продвинутый · выбери корпу', 'Advanced · pick a corp');
  const m = type.match(/^(main|advanced|basic)_(deus|syntetics|global|livlife)$/);
  if (m) {
    const isAdvanced = m[1] === 'advanced';
    const tier = isAdvanced ? t('Продвинутый', 'Advanced') : t('Основной', 'Standard');
    const facRu: Record<string,string> = { deus: 'Деус', syntetics: 'Синтетикс', global: 'Глобал', livlife: 'Лив Лайф' };
    const facEn: Record<string,string> = { deus: 'Deus', syntetics: 'Synthetics', global: 'Global', livlife: 'Liv Life' };
    const fac = t(facRu[m[2]] || m[2], facEn[m[2]] || m[2]);
    return `${tier} · ${fac}`;
  }
  return type;
}

// Контейнеры этих типов требуют выбора фракции при открытии.
function needsFactionPick(type: string): boolean {
  return type === 'main_faction' || type === 'advanced_faction';
}

// Маппинг типа контейнера к ContainerIcon-типу (иконка ШК).
// Для legacy basic_*/`main_faction`/`advanced_faction` — рисуем как фракционный.
function containerIconType(type: string): string {
  if (type === 'basic_common') return 'main_common';
  if (type === 'main_faction' || type === 'advanced_faction') {
    return type === 'main_faction' ? 'main_faction' : 'advanced_faction';
  }
  return type;
}

// МАГ.3: цвет рамки для карты по редкости (по дизайн-системе v0.1).
// Эпик теперь фиолетовый (#C93DFF), как в стайл-гайде; раньше был
// розовый (--neon-mg) — это была ошибка.
const RARITY_BORDER: Record<RarityApi, string> = {
  common:    'var(--r-common)',
  rare:      'var(--r-rare)',
  epic:      'var(--r-epic)',
  legendary: 'var(--r-legend)'
};

interface ContainerItem { id: number; type: string; source: string }

export function InventoryScreen() {
  const [tab, setTab] = useState<InvTab>('avatars');
  const cosmetics = useGameStore(s => s.cosmetics);
  const activeAvatarId = useGameStore(s => s.activeAvatarId);
  const setAvatarLocal = useGameStore(s => s.setActiveAvatarLocal);
  const titles = useGameStore(s => s.titles);
  const activeTitle = useGameStore(s => s.activeTitle);
  const setActiveTitleLocal = useGameStore(s => s.setActiveTitleLocal);
  const { hapticTap, hapticSuccess, hapticError, alert } = useTelegram();
  const toast = useToast(s => s.show);

  // ФТУЕ.6: tooltip-флоу первого входа в Инвентарь.
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  useTooltipFlow('inventory', () => [
    {
      id: 'inv-intro',
      coach: {
        title: tr('Инвентарь', 'Inventory'),
        body: tr(
          'Тут вся твоя коллекция: купленные/полученные Герои, карты, нераспакованные ШК, аватарки и титулы. Вкладки сверху переключают категории.',
          'Your full collection: bought/earned Heroes, cards, unopened Cipher Crates, avatars and titles. Top tabs switch categories.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'inv-shk',
      coach: {
        title: tr('Шифроконтейнеры', 'Cipher Crates'),
        body: tr(
          'На вкладке «ШК» — все купленные паки, которые ты ещё не вскрыл. Тап «Открыть» → анимация открытия с реальной выдачей карт. Эпики/легендарки подсвечиваются ярче.',
          'On the «CC» tab — all bought packs you haven\'t opened. Tap «Open» → opening animation with real card drops. Epics/Legendaries glow brighter.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'inv-cosmetics',
      coach: {
        title: tr('Аватарки и титулы', 'Avatars & titles'),
        body: tr(
          'Косметика — добывается за уровни аккаунта, БП и достижения. Тап по аватарке/титулу — становится активным (виден в Профиле и матчах). Часть косметики — эксклюзивные сезонные, не повторяются.',
          'Cosmetics — earned via account levels, BP and achievements. Tap an avatar/title — becomes active (shown in Profile and matches). Some are exclusive seasonal, never repeat.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  // МАГ.3: список купленных нераспакованных ШК + модалка открытия.
  const [containers, setContainers] = useState<ContainerItem[]>([]);
  const [openingId, setOpeningId] = useState<number | null>(null);
  // МАГ.3 legacy-fix: модалка выбора корпы для контейнеров main_faction/
  // advanced_faction (фракция не зашита в имени).
  const [factionPickContainer, setFactionPickContainer] = useState<ContainerItem | null>(null);
  const [revealed, setRevealed] = useState<{
    cards: OpenedCard[];
    pityTriggered: { epic: boolean; leg: boolean };
  } | null>(null);
  // ОТК.1: полная анимационная сцена (8s). Когда юзер в настройках выключает
  // "Анимации интерфейса" — пропускаем сцену и сразу показываем результат
  // в RevealedCardsModal.
  const animationsEnabled = useAnimationsEnabled();
  const [scene, setScene] = useState<{
    cards: OpenedCard[];
    pityTriggered: { epic: boolean; leg: boolean };
    crateType: ReturnType<typeof containerTypeToCrateType>;
  } | null>(null);

  const refreshContainers = useCallback(async () => {
    try {
      const p = await getProfile();
      setContainers(p.shifrokontainers || []);
    } catch { /* offline */ }
  }, []);
  useEffect(() => { refreshContainers(); }, [refreshContainers]);

  const doOpenPack = async (containerId: number, faction?: 'deus' | 'syntetics' | 'global' | 'livlife') => {
    if (openingId) return;
    hapticTap();
    setOpeningId(containerId);
    try {
      // Запоминаем тип контейнера ДО запроса (он удалится из списка после open).
      const cont = containers.find(c => c.id === containerId);
      const crateType = cont ? containerTypeToCrateType(cont.type) : 'generic';
      const r = await openPackApi(containerId, faction);
      hapticSuccess();
      if (animationsEnabled) {
        // Сначала играем сцену; revealed-модалка откроется при Claim/Skip.
        setScene({ cards: r.cards, pityTriggered: r.pity_triggered, crateType });
      } else {
        setRevealed({ cards: r.cards, pityTriggered: r.pity_triggered });
      }
      await refreshContainers();
    } catch (e: any) {
      hapticError();
      toast(tr(`Не открылось: ${e?.message || 'ошибка'}`, `Failed to open: ${e?.message || 'error'}`), 'red');
    } finally {
      setOpeningId(null);
    }
  };

  const onOpenPack = (c: ContainerItem) => {
    // Legacy-контейнер без зашитой фракции → сначала выбор корпы.
    if (needsFactionPick(c.type)) {
      setFactionPickContainer(c);
      return;
    }
    doOpenPack(c.id);
  };

  // Считаем реальные cosmetics по типам.
  const countsByTab: Record<InvTab, number> = {
    avatars: cosmetics.filter(c => c.cosmetic_type === 'avatar').length,
    titles: titles.length,
    frames: cosmetics.filter(c => c.cosmetic_type === 'frame').length,
    card_backs: cosmetics.filter(c => c.cosmetic_type === 'card_back').length,
    emote_sets: cosmetics.filter(c => c.cosmetic_type === 'emote').length,
    card_skins: cosmetics.filter(c => c.cosmetic_type === 'card_skin').length,
    packs: containers.length
  };
  const ALL_INV_TABS: InvTab[] = ['avatars','titles','frames','card_backs','emote_sets','card_skins','packs'];
  const owned = ALL_INV_TABS.reduce((s, k) => s + countsByTab[k], 0);
  const total = 48; // условный потолок прогресс-бара (косметика в игре)
  const pct = Math.min(100, Math.round((owned / total) * 100));

  const onPickAvatar = async (avatarId: string) => {
    hapticTap();
    try {
      await setActiveAvatar(avatarId);
      setAvatarLocal(avatarId);
      hapticSuccess();
    } catch (e: any) {
      hapticError();
      alert(tr('Не удалось сменить аватар: ', "Couldn't change avatar: ") + (e?.message || tr('ошибка', 'error')));
    }
  };

  const onPickTitle = async (titleId: number | null, titleObj?: { id: number; title_code: string; title_text_ru: string }) => {
    hapticTap();
    try {
      await setActiveTitle(titleId);
      setActiveTitleLocal(titleId ? (titleObj as any) : null);
      hapticSuccess();
    } catch (e: any) {
      hapticError();
      alert(tr('Не удалось сменить прозвище: ', "Couldn't change title: ") + (e?.message || tr('ошибка', 'error')));
    }
  };

  const invTabLabel = buildInvTabLabel(tr);

  return (
    <ScreenLayout title={tr('ИНВЕНТАРЬ', 'INVENTORY')} backTo="/profile">
      <div className={styles.progressBar}>
        <div className={styles.progressTop}>
          <span className={styles.progressLabel}>{tr('Открыто', 'Unlocked')}</span>
          <span className={styles.progressCount}>
            <strong>{owned}</strong> <span style={{ color: 'var(--t-lo)' }}>/ {total}</span>
          </span>
          <span className={styles.progressPct}>{pct}%</span>
        </div>
        <div className={styles.progressBarBg}>
          <div className={styles.progressBarFill} style={{ width: `${pct}%`, background: 'var(--neon-mg)' }} />
        </div>
      </div>

      <div className={styles.invTabGrid}>
        {ALL_INV_TABS.map(k => (
          <button
            key={k}
            className={`${styles.invTab} ${tab === k ? styles.invTabActive : ''}`}
            onClick={() => setTab(k)}
          >
            {invTabLabel[k]}<span className={styles.invTabNum}>{countsByTab[k]}</span>
          </button>
        ))}
      </div>

      <div className={styles.invGrid}>
        {tab === 'titles' ? (
          countsByTab.titles === 0 ? (
            <div className={styles.empty}>
              <div className="cd" style={{ color: 'var(--t-lo)', fontSize: 11 }}>{tr('ПУСТО', 'EMPTY')}</div>
            </div>
          ) : (
            <div style={{
              gridColumn: '1 / -1',
              display: 'flex', flexDirection: 'column', gap: 6
            }}>
              {/* «Без прозвища» */}
              <button
                onClick={() => onPickTitle(null)}
                className="cut-s"
                style={titleRowStyle(!activeTitle)}
              >
                <span style={{ fontSize: 13 }}>
                  {!activeTitle ? '✓ ' : ''}{tr('Без прозвища', 'No title')}
                </span>
                <span style={{ fontSize: 10, color: 'var(--t-lo)' }}>{tr('скрыть под ником', 'hide under handle')}</span>
              </button>
              {titles.map(t => {
                const isActive = activeTitle?.id === t.id;
                // EN: маппим title_code → EN-имя через runtime-словарь.
                const titleText = trTitleText(t.title_code, t.title_text_ru);
                return (
                  <button
                    key={t.id}
                    onClick={() => onPickTitle(t.id, { id: t.id, title_code: t.title_code, title_text_ru: t.title_text_ru })}
                    className="cut-s"
                    style={titleRowStyle(isActive)}
                  >
                    <span style={{ fontSize: 13 }}>
                      {isActive ? '✓ ' : ''}· {titleText}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--t-lo)' }}>{t.source}</span>
                  </button>
                );
              })}
            </div>
          )
        ) : tab === 'avatars' ? (
          countsByTab.avatars === 0 ? (
            <div className={styles.empty}>
              <div className="cd" style={{ color: 'var(--t-lo)', fontSize: 11, marginBottom: 4 }}>{tr('ПУСТО', 'EMPTY')}</div>
            </div>
          ) : (
            cosmetics
              .filter(c => c.cosmetic_type === 'avatar')
              .map(c => {
                const isActive = c.cosmetic_id === activeAvatarId;
                return (
                  <button
                    key={c.id}
                    onClick={() => onPickAvatar(c.cosmetic_id)}
                    className={`${styles.invCell} cut-s`}
                    style={{
                      padding: 0,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      padding: 8,
                      outline: isActive ? '2px solid var(--neon-cy)' : 'none',
                      outlineOffset: -2
                    }}>
                      <UserAvatar avatarId={c.cosmetic_id} size={64} />
                    </div>
                    <div className={styles.invCellLbl}>
                      {isActive ? '✓ ' : ''}{c.cosmetic_id}
                    </div>
                  </button>
                );
              })
          )
        ) : tab === 'packs' ? (
          containers.length === 0 ? (
            <div className={styles.empty}>
              <div className="cd" style={{ color: 'var(--t-lo)', fontSize: 11, marginBottom: 4 }}>{tr('ПУСТО', 'EMPTY')}</div>
              <div className="cb" style={{ color: 'var(--t-mute)', fontSize: 11 }}>
                {tr(
                  'Купи Шифроконтейнер в Магазине (вкладка ШК).',
                  'Buy a Cipher Crate in the Shop (CC tab).'
                )}
              </div>
            </div>
          ) : (
            // ШК — отдельная сетка не invGrid (нужны более крупные плитки + кнопка).
            <div style={{
              gridColumn: '1 / -1',
              display: 'flex', flexDirection: 'column', gap: 6
            }}>
              {containers.map(c => {
                const factionPickRequired = needsFactionPick(c.type);
                return (
                  <div
                    key={c.id}
                    className="cut-s"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: 10,
                      background: 'rgba(255,255,255,0.03)',
                      boxShadow: `inset 0 0 0 0.5px ${factionPickRequired ? 'var(--neon-am)' : 'var(--line)'}`
                    }}
                  >
                    <div style={{ flexShrink: 0 }}>
                      <ContainerIcon type={containerIconType(c.type) as any} size={42} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 12,
                        color: factionPickRequired ? 'var(--neon-am)' : 'var(--t-hi)',
                        letterSpacing: '0.04em'
                      }}>
                        {containerLabel(c.type, tr)}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--t-lo)', marginTop: 2 }}>
                        #{c.id} · {c.source === 'shop' ? tr('из магазина', 'from shop') : c.source}
                      </div>
                    </div>
                    <button
                      onClick={() => onOpenPack(c)}
                      disabled={openingId === c.id}
                      style={{
                        padding: '8px 14px',
                        background: openingId === c.id ? 'var(--bg)' : 'var(--neon-li)',
                        color: openingId === c.id ? 'var(--t-lo)' : 'var(--bg)',
                        border: 'none', fontSize: 11, fontWeight: 700,
                        letterSpacing: '0.08em', fontFamily: 'inherit',
                        cursor: openingId === c.id ? 'wait' : 'pointer'
                      }}
                    >
                      {openingId === c.id ? '…' : tr('ОТКРЫТЬ', 'OPEN')}
                    </button>
                  </div>
                );
              })}
            </div>
          )
        ) : countsByTab[tab] === 0 ? (
          <div className={styles.empty}>
            <div className="cd" style={{ color: 'var(--t-lo)', fontSize: 11, marginBottom: 4 }}>
              {tr('ПУСТО', 'EMPTY')}
            </div>
            <div className="cb" style={{ color: 'var(--t-mute)', fontSize: 11 }}>
              {tr('Купи в Магазине или получи за достижения', 'Buy in Shop or earn via achievements')}
            </div>
          </div>
        ) : (
          Array.from({ length: countsByTab[tab] }).map((_, i) => (
            <div key={i} className={`${styles.invCell} cut-s`}>
              <div className={styles.invCellInner}>
                {tab === 'frames' && '⬜'}
                {tab === 'card_backs' && '🂠'}
                {tab === 'emote_sets' && '😈'}
                {tab === 'card_skins' && '🃏'}
              </div>
              <div className={styles.invCellLbl}>#{i + 1}</div>
            </div>
          ))
        )}
      </div>

      {/* МАГ.3 legacy-fix: модалка выбора фракции для контейнеров без
          зашитой корпы (main_faction / advanced_faction). */}
      {factionPickContainer && (
        <FactionPickerModal
          container={factionPickContainer}
          onClose={() => setFactionPickContainer(null)}
          onPick={(faction) => {
            const id = factionPickContainer.id;
            setFactionPickContainer(null);
            doOpenPack(id, faction);
          }}
        />
      )}

      {/* ОТК.1: полная анимация открытия ШК (8s, см. CrateOpeningScene).
          После Claim/Skip — сразу закрываем, без старой модалки RevealedCardsModal.
          Игрок видит карты в самой сцене (карты разложены 2x4 + Summary chip).
          Подробный просмотр конкретных карт — позже в Инвентаре (ИНВ.1). */}
      {scene && (
        <CrateOpeningScene
          cards={scene.cards}
          crateType={scene.crateType}
          onClaim={() => setScene(null)}
          onSkip={() => setScene(null)}
        />
      )}

      {/* Legacy RevealedCardsModal — используется ТОЛЬКО когда анимации
          выключены в Settings. С анимациями flow закрывается сразу
          после CrateOpeningScene. */}
      {revealed && (
        <RevealedCardsModal
          cards={revealed.cards}
          pityTriggered={revealed.pityTriggered}
          onClose={() => setRevealed(null)}
        />
      )}
    </ScreenLayout>
  );
}

// МАГ.3 legacy-fix: модалка выбора корпы для контейнеров main_faction /
// advanced_faction (фракция не зашита при создании пака — пришёл из
// квестов / БП / наград уровня и т.п.).
function FactionPickerModal({ container, onClose, onPick }: {
  container: ContainerItem;
  onClose: () => void;
  onPick: (faction: 'deus' | 'syntetics' | 'global' | 'livlife') => void;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  type F = 'deus' | 'syntetics' | 'global' | 'livlife';
  const FAC_COLOR: Record<F, string> = {
    deus: 'var(--deus)', syntetics: 'var(--syn)',
    global: 'var(--gsec)', livlife: 'var(--liv)'
  };
  const FAC_LABEL: Record<F, string> = isEn
    ? { deus: 'Deus', syntetics: 'Synthetics', global: 'Global', livlife: 'Liv Life' }
    : { deus: 'Деус', syntetics: 'Синтетикс', global: 'Глобал', livlife: 'Лив Лайф' };
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.88)',
        zIndex: 350,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-2, #101018)',
          width: '100%', maxWidth: 380,
          boxShadow: 'inset 0 0 0 0.5px var(--neon-am)',
          padding: 18,
          display: 'flex', flexDirection: 'column', gap: 12
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          color: 'var(--neon-am)', fontSize: 12, letterSpacing: '0.14em',
          fontWeight: 700, textAlign: 'center'
        }}>
          ► {tr('ВЫБЕРИ КОРПУ', 'PICK A CORP')}
        </div>
        <div style={{
          color: 'var(--t-md)', fontSize: 12, lineHeight: 1.5,
          textAlign: 'center'
        }}>
          {tr(
            `ШК #${container.id} (${containerLabel(container.type, tr)}) содержит карты ОДНОЙ корпы. Какую открываем?`,
            `CC #${container.id} (${containerLabel(container.type, tr)}) contains cards of ONE corp. Which one do we open?`
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {(['deus','syntetics','global','livlife'] as F[]).map(f => (
            <button
              key={f}
              onClick={() => onPick(f)}
              style={{
                padding: '12px 8px',
                background: 'var(--bg)',
                color: FAC_COLOR[f],
                border: 'none',
                fontSize: 12, fontWeight: 700,
                letterSpacing: '0.08em',
                fontFamily: 'inherit',
                cursor: 'pointer',
                boxShadow: `inset 0 0 0 0.5px ${FAC_COLOR[f]}`
              }}
            >
              {FAC_LABEL[f]}
            </button>
          ))}
        </div>
        <button
          onClick={onClose}
          style={{
            padding: 8, background: 'transparent', color: 'var(--t-md)',
            border: '0.5px solid var(--t-md)',
            fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
            letterSpacing: 1
          }}
        >{tr('ОТМЕНА', 'CANCEL')}</button>
      </div>
    </div>
  );
}

// МАГ.3c: модалка открытия пака с тремя фазами на каждой карте:
//   back         — лежит рубашкой, видна только тонкая рамка по редкости.
//   highlighted  — после 1-го тапа, рамка ярче + пульс по цвету редкости.
//   revealed     — после 2-го тапа, flip-анимация по Y → видно содержимое.
// Дополнительно:
//   - glow-волна слева направо для rare/epic/legendary на момент flip;
//   - тряска экрана (контейнера модалки) при переворачивании легендарки.
type CardPhase = 'back' | 'highlighted' | 'revealed';

function RevealedCardsModal({ cards, pityTriggered, onClose }: {
  cards: OpenedCard[];
  pityTriggered: { epic: boolean; leg: boolean };
  onClose: () => void;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const rarityNameRu: Record<RarityApi, string> = isEn
    ? { common: 'Com.', rare: 'Rare', epic: 'Epic', legendary: 'Leg.' }
    : { common: 'Об.', rare: 'Ред.', epic: 'Эпик', legendary: 'Лег.' };
  // МАГ.3 fix: полные названия корп цветом этой корпы (две строки).
  // По дизайн-системе v0.1: первая строка — основной цвет корпы, вторая —
  // акцентный (например, Syntetics: фиолетовый + кислотно-жёлтый).
  const FACTION_DISPLAY: Record<string, { line1: string; line2: string; color1: string; color2: string }> = {
    deus:      { line1: 'DEUS',      line2: 'SYSTEMS',   color1: 'var(--deus)', color2: 'var(--deus-2)' },
    syntetics: { line1: 'SYNTETICS', line2: 'SYNDICATE', color1: 'var(--syn)',  color2: 'var(--syn-2)' },
    global:    { line1: 'GLOBAL',    line2: 'SECURITY',  color1: 'var(--gsec)', color2: 'var(--gsec-2)' },
    livlife:   { line1: 'LIV',       line2: 'LIFE',      color1: 'var(--liv)',  color2: 'var(--liv-2)' },
    neutral:   { line1: 'STREET',    line2: 'NEUTRAL',   color1: 'var(--neu)',  color2: 'var(--neu-2)' }
  };

  // Фазы по карте — изначально все back.
  const [phases, setPhases] = useState<CardPhase[]>(() => cards.map(() => 'back'));
  // Триггер тряски: меняем ключ — CSS-анимация перезапускается.
  const [shakeKey, setShakeKey] = useState(0);

  const onCardTap = (i: number) => {
    setPhases(prev => {
      const next = [...prev];
      if (next[i] === 'back') {
        next[i] = 'highlighted';
      } else if (next[i] === 'highlighted') {
        next[i] = 'revealed';
        // Тряска при перевороте легендарки.
        if (cards[i].rarity === 'legendary') {
          setShakeKey(k => k + 1);
        }
      }
      return next;
    });
  };

  const allRevealed = phases.every(p => p === 'revealed');

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.92)',
        zIndex: 400,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12,
        animation: 'cardsBackdropIn 0.25s ease-out'
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes cardsBackdropIn {
          from { background: rgba(0,0,0,0); }
          to   { background: rgba(0,0,0,0.92); }
        }
        /* Каскад появления карт сверху вниз. */
        @keyframes cardSlideIn {
          0%   { opacity: 0; transform: translateY(-30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        /* Пульсация рамки на фазе highlighted: ярче-тусклее. */
        @keyframes phasePulse {
          0%, 100% { box-shadow: inset 0 0 0 1.5px currentColor, 0 0 8px currentColor; }
          50%      { box-shadow: inset 0 0 0 1.5px currentColor, 0 0 22px currentColor; }
        }
        /* Flip — поворот container'а по Y. Применяется на .card3d. */
        @keyframes flipCard {
          0%   { transform: rotateY(0); }
          100% { transform: rotateY(180deg); }
        }
        /* Glow-волна — двигается слева направо по карте после flip.
           Используем градиент-полосу через background-position. */
        @keyframes glowSweep {
          0%   { background-position: -150% 0; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { background-position: 250% 0; opacity: 0; }
        }
        /* Тряска модального контейнера. */
        @keyframes shakeContainer {
          0%, 100% { transform: translate(0, 0); }
          10%      { transform: translate(-8px, 5px); }
          25%      { transform: translate(7px, -4px); }
          40%      { transform: translate(-5px, -3px); }
          55%      { transform: translate(6px, 6px); }
          70%      { transform: translate(-4px, 3px); }
          85%      { transform: translate(3px, -2px); }
        }

        .reveal-row {
          opacity: 0;
          animation: cardSlideIn 0.45s ease-out forwards;
        }
        .card3d-wrap {
          perspective: 1200px;
          position: relative;
          width: 100%;
          height: 56px;
        }
        .card3d {
          position: absolute; inset: 0;
          transform-style: preserve-3d;
          transition: transform 0.65s cubic-bezier(0.45, 0, 0.25, 1);
          cursor: pointer;
        }
        .card3d.is-flipped {
          transform: rotateY(180deg);
        }
        .card-face {
          position: absolute; inset: 0;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          display: flex;
          align-items: center;
          padding: 10px 12px;
          background: var(--bg, #0A0A12);
          color: currentColor;
        }
        .card-face.card-back {
          font-family: 'Chakra Petch', sans-serif;
          font-size: 10px;
          letter-spacing: 0.14em;
          color: currentColor;
          justify-content: center;
          gap: 10px;
          opacity: 0.85;
        }
        .card-face.card-front {
          transform: rotateY(180deg);
          gap: 10px;
        }
        /* МАГ.3c fix: на фазе back редкость НЕ должна просвечивать.
           Нейтральная рамка, нейтральный символ. Цвет редкости появляется
           только после 1-го тапа (highlighted). */
        .phase-back .card3d {
          box-shadow: inset 0 0 0 1px var(--t-mute, #3a3a4a);
          color: var(--t-md);
        }
        .phase-back .card-back .corpo-glyph,
        .phase-back .card-back .corpo-text {
          color: var(--t-md);
        }
        .phase-highlighted .card3d {
          animation: phasePulse 1.4s ease-in-out infinite;
        }
        /* После 1-го тапа — рамка получает currentColor (= цвет редкости),
           глиф и текст КОРПО-ШИФР тоже подкрашиваются. */
        .phase-highlighted .card-back .corpo-glyph,
        .phase-highlighted .card-back .corpo-text,
        .phase-revealed .card-back .corpo-glyph,
        .phase-revealed .card-back .corpo-text {
          color: currentColor;
        }
        /* Glow-волна для редких/эпик/лег — наложение поверх front. */
        .glow-sweep {
          position: absolute; inset: 0;
          pointer-events: none;
          background: linear-gradient(
            105deg,
            transparent 30%,
            currentColor 50%,
            transparent 70%
          );
          background-size: 60% 100%;
          background-repeat: no-repeat;
          mix-blend-mode: screen;
          opacity: 0;
        }
        .glow-sweep.active {
          animation: glowSweep 0.9s ease-in-out 0.3s 1;
        }
        .shake {
          animation: shakeContainer 0.55s cubic-bezier(0.36, 0.07, 0.19, 0.97);
        }
      `}</style>

      <div
        key={shakeKey}
        className={shakeKey > 0 ? 'shake' : ''}
        style={{
          background: 'var(--bg-2, #101018)',
          width: '100%', maxWidth: 400,
          boxShadow: 'inset 0 0 0 0.5px var(--neon-cy)',
          padding: 18,
          display: 'flex', flexDirection: 'column', gap: 10,
          maxHeight: '92vh', overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          color: 'var(--neon-cy)', fontSize: 13, letterSpacing: '0.14em',
          fontWeight: 700, textAlign: 'center'
        }}>
          ► {tr('ПАК РАСПАКОВАН', 'PACK OPENED')}
        </div>

        <div style={{
          fontSize: 10, color: 'var(--t-lo)', textAlign: 'center',
          letterSpacing: '0.06em', marginTop: -4
        }}>
          {!allRevealed
            ? tr(
              'Тапни карту — подсветится. Ещё раз — перевернётся.',
              'Tap a card — it lights up. Tap again — flips.'
            )
            : tr(
              'Все карты вскрыты. Забирай в коллекцию.',
              'All cards revealed. Take them to collection.'
            )}
        </div>

        {(pityTriggered.epic || pityTriggered.leg) && (
          <div style={{
            padding: '6px 10px',
            background: 'var(--bg, #0A0A12)',
            color: pityTriggered.leg ? 'var(--neon-am)' : 'var(--neon-mg)',
            boxShadow: `inset 0 0 0 0.5px ${pityTriggered.leg ? 'var(--neon-am)' : 'var(--neon-mg)'}`,
            fontSize: 11, fontWeight: 700,
            letterSpacing: '0.08em', textAlign: 'center'
          }}>
            ✨ {pityTriggered.leg
              ? tr('СРАБОТАЛА ГАРАНТИЯ ЛЕГА', 'LEGENDARY GUARANTEE TRIGGERED')
              : tr('СРАБОТАЛА ГАРАНТИЯ ЭПИКА', 'EPIC GUARANTEE TRIGGERED')}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          {cards.map((c, i) => {
            const color = RARITY_BORDER[c.rarity];
            const phase = phases[i];
            const isRareOrBetter =
              c.rarity === 'rare' || c.rarity === 'epic' || c.rarity === 'legendary';
            return (
              <div
                key={i}
                className={`reveal-row phase-${phase}`}
                style={{
                  animationDelay: `${i * 120}ms`,
                  // currentColor пробрасываем дальше в .card3d
                  color
                }}
              >
                <div className="card3d-wrap">
                  <div
                    className={`card3d ${phase === 'revealed' ? 'is-flipped' : ''}`}
                    onClick={() => onCardTap(i)}
                  >
                    {/* Рубашка (back) — на фазе back ничего о редкости не
                        говорит (нейтральные стили из CSS). Цвет/глиф редкости
                        проступают только после 1-го тапа. */}
                    <div className="card-face card-back">
                      <span className="corpo-glyph" style={{ fontWeight: 700, fontSize: 11 }}>
                        ◆
                      </span>
                      <span className="corpo-text" style={{
                        fontWeight: 700, fontSize: 11, letterSpacing: '0.14em'
                      }}>
                        {tr('КОРПО-ШИФР', 'CORP-CIPHER')}
                      </span>
                      <span style={{
                        color: 'var(--t-lo)', fontSize: 9, letterSpacing: '0.06em',
                        marginLeft: 4
                      }}>
                        {phase === 'highlighted' ? tr('ТАПНИ ЕЩЁ', 'TAP AGAIN') : tr('ТАПНИ', 'TAP')}
                      </span>
                    </div>
                    {/* Лицо (front) — после flip. */}
                    <div
                      className="card-face card-front"
                      style={{ boxShadow: `inset 0 0 0 1.5px ${color}` }}
                    >
                      <div style={{
                        fontSize: 10, color, letterSpacing: '0.08em',
                        minWidth: 38, textAlign: 'center', fontWeight: 700
                      }}>
                        {rarityNameRu[c.rarity]}
                      </div>
                      <div style={{ flex: 1, fontSize: 13, color: 'var(--t-hi)' }}>
                        {isEn ? ((c as any).name_en || c.name_ru) : c.name_ru}
                      </div>
                      {(() => {
                        const f = FACTION_DISPLAY[c.faction] || FACTION_DISPLAY.neutral;
                        return (
                          <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                            fontSize: 9, letterSpacing: '0.08em', fontWeight: 700,
                            lineHeight: 1.15, minWidth: 56, textAlign: 'right'
                          }}>
                            <span style={{ color: f.color1 }}>{f.line1}</span>
                            <span style={{ color: f.color2 }}>{f.line2}</span>
                          </div>
                        );
                      })()}
                      {/* Glow-волна — рендерим только для rare+, активна когда
                          карта в revealed-фазе. */}
                      {isRareOrBetter && (
                        <div className={`glow-sweep ${phase === 'revealed' ? 'active' : ''}`} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={onClose}
          style={{
            padding: 12,
            background: allRevealed ? 'var(--neon-li)' : 'var(--bg)',
            color: allRevealed ? 'var(--bg)' : 'var(--t-md)',
            border: 'none',
            fontSize: 12, fontWeight: 700,
            letterSpacing: '0.1em',
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginTop: 8,
            boxShadow: allRevealed ? 'none' : 'inset 0 0 0 0.5px var(--t-md)'
          }}
        >{allRevealed
          ? tr('ЗАБРАТЬ В КОЛЛЕКЦИЮ', 'TAKE TO COLLECTION')
          : tr('ЗАКРЫТЬ (НЕ ОТКРЫВАЯ)', 'CLOSE (KEEP UNOPENED)')}</button>
      </div>
    </div>
  );
}

// Стиль строки прозвища в Inventory → таб «Прозвища».
function titleRowStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.03)',
    border: 'none',
    outline: active ? '2px solid var(--neon-cy)' : 'none',
    outlineOffset: -2,
    color: 'var(--t-hi)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    width: '100%',
    textAlign: 'left'
  };
}
